import ApiClient from '@/api/ApiClient';

import paramsSerializer from '@/utils/apiUtils';
import type {
  AbsenceDeleteDto,
  AbsenceDto,
  CalendarValidationRequest,
  Holiday,
  HolidayCalendar,
  LocalDate,
  ScheduleDto,
  ScheduleRowsResponse,
  SortCriteria,
  ValidationResult,
} from '../models';

export type GetDaysBetweenRequest = {
  end?: LocalDate;
  start?: LocalDate;
  subType?: string;
  userId?: string;
};

export type GetScheduleRowsResponseRequest = {
  companyNames?: string[];
  departmentIds?: string[];
  endDate?: LocalDate;
  groupIds?: string[];
  managerIds?: string[];
  positions?: string[];
  sortCriteria?: SortCriteria[];
  startDate?: LocalDate;
  subTypes?: string[];
  tagIds?: number[];
  userIds?: string[];
  year?: number;
};

export class CalendarClient {
  apiClient: ApiClient;

  constructor(apiClient?: ApiClient) {
    this.apiClient = apiClient || ApiClient.instance;
  }

  insertAbsences(request: { body?: AbsenceDto[]; } = {}): Promise<void> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/calendar/absences',
      body,
    });
  }

  deleteAbsences(request: { body?: AbsenceDeleteDto; } = {}): Promise<void> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/calendar/absences/delete',
      body,
    });
  }

  submitSchedule(request: { body?: ScheduleDto; } = {}): Promise<void> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/calendar/absences/submitSchedule',
      body,
    });
  }

  updateStatusPlanned(request: { body?: AbsenceDeleteDto; } = {}): Promise<void> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/calendar/absences/updateStatusPlanned',
      body,
    });
  }

  getAllHolidays(request: { countries?: string[]; end?: LocalDate; start?: LocalDate; } = {}): Promise<Holiday[]> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/calendar/holidays',
      params,
      paramsSerializer,
    });
  }

  addCalendar(request: { body?: HolidayCalendar; } = {}): Promise<void> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/calendar/holidays',
      body,
    });
  }

  getDaysBetween(request: GetDaysBetweenRequest = {}): Promise<number> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/calendar/holidays/daysbetween',
      params,
    });
  }

  getHolidays(request: { country: string; end?: LocalDate; start?: LocalDate; }): Promise<Holiday[]> {
    const { country, ...params } = request;
    return this.apiClient.get({
      path: `/api/calendar/holidays/${country}`,
      params,
    });
  }

  getScheduleRowsResponse(request: GetScheduleRowsResponseRequest = {}): Promise<ScheduleRowsResponse> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/calendar/scheduleRows',
      params,
      paramsSerializer,
    });
  }

  validateAbsences(request: { body?: CalendarValidationRequest; } = {}): Promise<ValidationResult> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/calendar/validate',
      body,
    });
  }
}

export default CalendarClient;
